/**
 * AppShell — frame-and-card layout.
 *
 * Visual model (matches the GitLab reference the owner attached):
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ topbar — full width, transparent (inherits canvas bg)             │
 *   │ [logo]       [   SEARCH centered   ]      [profile cluster]       │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │  sidebar │  padding │  ┌────────────────────────────────────┐ │   │
 *   │  (also   │          │  │ white content card                 │ │   │
 *   │  shares  │          │  │ rounded-2xl, shadow, border        │ │   │
 *   │  canvas) │          │  │                                    │ │   │
 *   │          │          │  └────────────────────────────────────┘ │   │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Page bg, sidebar bg, topbar bg are ALL the canvas color — a single
 * continuous "frame." There is NO visual separation between sidebar
 * and topbar (no border, no shadow). The content card is the only
 * surface-colored region, with its own rounded corners + shadow.
 *
 * Mobile (< lg):
 *   - Sidebar becomes an overlay drawer (still on canvas bg)
 *   - The padding around the content card collapses to 0 — full-bleed
 *   - Topbar stays at the top, full-width
 */

import { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import Sidebar from './Sidebar'
import Header from './Header'
import WorkspaceBanner from '../workspace/WorkspaceBanner'
import SubscriptionBanners from '../banners/SubscriptionBanners'
import { Breadcrumbs } from '../ui'

export default function AppShell({ children }) {
  const { isWorkspaceSwitched } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Close the mobile drawer when transitioning back to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const toggleSidebar = () => setSidebarOpen(o => !o)
  const closeSidebar  = () => setSidebarOpen(false)

  return (
    <div
      className="w-screen overflow-hidden bg-canvas flex flex-col"
      // Height = viewport minus the fixed dev-banner that lives above us
      // in App.jsx. CSS variable comes from tokens.css and defaults to 30px.
      style={{ height: 'calc(100vh - var(--app-top-offset, 0px))' }}
    >
      {/* Optional workspace banner — sits at the very top above the topbar */}
      {isWorkspaceSwitched && <WorkspaceBanner />}

      {/* Topbar — spans full width, transparent (inherits canvas) */}
      <Header onMenuClick={toggleSidebar} />

      {/* Below topbar: sidebar (canvas) + padded content card (surface) */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — fixed drawer on mobile, static rail on desktop */}
        <div
          className={`
            fixed inset-y-0 start-0 z-40 lg:static lg:inset-auto
            transition-transform duration-200 ease-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>

        {/* Content area — padded so the white card sits as an inset
            rectangle inside the canvas frame. On mobile, padding collapses
            so the content goes edge-to-edge below the topbar. */}
        <div className="flex-1 overflow-hidden lg:px-3 lg:pb-3">
          <main className="h-full overflow-auto bg-surface lg:rounded-2xl lg:shadow-token-md lg:border lg:border-border">
            <div className="px-4 sm:px-6 py-4 sm:py-5 max-w-7xl mx-auto">
              <SubscriptionBanners />
              <Breadcrumbs />
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile sidebar overlay backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
    </div>
  )
}
